import { format, endOfMonth, isWithinInterval, startOfMonth, subMonths } from 'date-fns';
import type { Expense, ExpenseCategory } from '@/lib/types';
import { CATEGORY_ICONS, CATEGORY_LABELS } from '@/lib/types';

export interface DashboardViewModel {
  month: {
    value: string;
    label: string;
    start: Date;
    end: Date;
  };
  stats: {
    totalSpent: number;
    totalTax: number;
    pending: number;
    reimbursed: number;
    donated: number;
    count: number;
    pendingCount: number;
    reimbursedCount: number;
    donatedCount: number;
    momDeltaPct: number | null;
  };
  categoryData: Array<{ category: ExpenseCategory; label: string; icon: string; amount: number }>;
  monthlyTrend: Array<{ month: string; total: number; reimbursed: number }>;
  recentExpenses: Expense[];
  monthExpenses: Expense[];
}

export function normalizeDashboardMonth(monthParam?: string) {
  return monthParam && /^\d{4}-\d{2}$/.test(monthParam) ? monthParam : format(new Date(), 'yyyy-MM');
}

export function resolveDefaultDashboardMonth(expenses: Expense[], now = new Date()) {
  const months = Array.from({ length: 6 }, (_, index) => {
    const monthBase = subMonths(now, index);
    const start = startOfMonth(monthBase);
    const end = endOfMonth(monthBase);
    const hasExpense = expenses.some((expense) =>
      isWithinInterval(new Date(expense.transaction_date), { start, end })
    );

    return {
      value: format(monthBase, 'yyyy-MM'),
      hasExpense,
    };
  });

  const match = months.find((month) => month.hasExpense);
  return match?.value ?? format(now, 'yyyy-MM');
}

export function buildDashboardViewModel(expenses: Expense[], monthParam?: string): DashboardViewModel {
  const monthValue = normalizeDashboardMonth(monthParam);
  const base = new Date(`${monthValue}-01T00:00:00`);
  const month = {
    value: monthValue,
    label: format(base, 'MMMM yyyy'),
    start: startOfMonth(base),
    end: endOfMonth(base),
  };

  const monthExpenses = expenses.filter((expense) =>
    isWithinInterval(new Date(expense.transaction_date), { start: month.start, end: month.end })
  );

  const prevBase = subMonths(base, 1);
  const prevMonthSpent = expenses
    .filter((expense) =>
      isWithinInterval(new Date(expense.transaction_date), { start: startOfMonth(prevBase), end: endOfMonth(prevBase) })
    )
    .reduce((sum, expense) => sum + expense.total, 0);
  const totalSpent = monthExpenses.reduce((sum, expense) => sum + expense.total, 0);

  const stats = {
    totalSpent,
    totalTax: monthExpenses.reduce((sum, expense) => sum + expense.tax, 0),
    pending: monthExpenses.filter((expense) => expense.status === 'pending').reduce((sum, expense) => sum + expense.total, 0),
    reimbursed: monthExpenses.filter((expense) => expense.status === 'reimbursed').reduce((sum, expense) => sum + expense.total, 0),
    donated: monthExpenses.filter((expense) => expense.status === 'donation').reduce((sum, expense) => sum + expense.total, 0),
    count: monthExpenses.length,
    pendingCount: monthExpenses.filter((expense) => expense.status === 'pending').length,
    reimbursedCount: monthExpenses.filter((expense) => expense.status === 'reimbursed').length,
    donatedCount: monthExpenses.filter((expense) => expense.status === 'donation').length,
    momDeltaPct: prevMonthSpent > 0 ? ((totalSpent - prevMonthSpent) / prevMonthSpent) * 100 : null,
  };

  const categoryMap = new Map<ExpenseCategory, number>();
  monthExpenses.forEach((expense) => {
    expense.items.forEach((item) => {
      categoryMap.set(item.category, (categoryMap.get(item.category) || 0) + item.total);
    });
  });

  const categoryData = Array.from(categoryMap.entries())
    .map(([category, amount]) => ({
      category,
      label: CATEGORY_LABELS[category],
      icon: CATEGORY_ICONS[category],
      amount: Number(amount.toFixed(2)),
    }))
    .sort((a, b) => b.amount - a.amount);

  const monthlyTrend = Array.from({ length: 6 }, (_, index) => {
    const monthBase = subMonths(base, 5 - index);
    const start = startOfMonth(monthBase);
    const end = endOfMonth(monthBase);
    const inMonth = expenses.filter((expense) =>
      isWithinInterval(new Date(expense.transaction_date), { start, end })
    );
    const total = inMonth.reduce((sum, expense) => sum + expense.total, 0);
    const reimbursed = inMonth
      .filter((expense) => expense.status === 'reimbursed')
      .reduce((sum, expense) => sum + expense.total, 0);

    return { month: format(monthBase, 'MMM'), total: Number(total.toFixed(2)), reimbursed: Number(reimbursed.toFixed(2)) };
  });

  return {
    month,
    stats,
    categoryData,
    monthlyTrend,
    recentExpenses: [...expenses].slice(0, 5),
    monthExpenses,
  };
}
